import {
    Button,
    Card,
    H3,
    H5,
    H6,
    HTMLTable,
    Tag,
    Tooltip
} from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import {
    formatPercentString,
    getFormattedCost
} from "components/common/constraintFormattingHelpers";
import { loader } from "graphql.macro";
import { getHasuraDataAndConvertToNiraType } from "graphql/helpers/queryHelpers";
import {
    AdvancedStudy,
    AdvancedStudyConstraintStack,
    convertHasuraAdvancedStudyResultsToNiraAdvancedStudyDetails,
    STATUS_TO_DISPLAY_STRING,
    STATUS_TO_INTENT
} from "types/advancedStudyType";
import { niraBusIdToBusId } from "types/busType";
import { MonitoredFacilityId } from "types/monitoredFacilityType";
import "./ViewAdvancedStudyResults.scss";

const GET_ADVANCED_STUDY_RESULTS = loader(
    "src/graphql/getAdvancedStudyResults.graphql"
);

const formatBenchPercentLoading = (percentLoading: number): string => {
    return percentLoading == 0 ? "< 85%" : `${percentLoading.toFixed(2)}%`;
};

interface ViewAdvancedStudyResultsProps {
    advancedStudy: AdvancedStudy;
    onBack: () => void;
}

const ViewAdvancedStudyResults: React.FC<ViewAdvancedStudyResultsProps> = (
    props
) => {
    const { advancedStudy, onBack } = props;

    const advancedStudyDetails = getHasuraDataAndConvertToNiraType(
        GET_ADVANCED_STUDY_RESULTS,
        convertHasuraAdvancedStudyResultsToNiraAdvancedStudyDetails,
        { advancedStudyId: advancedStudy.advancedStudyId },
        false,
        "network-only"
    );

    const advancedStudyStatus =
        advancedStudyDetails === "loading"
            ? advancedStudy.status
            : advancedStudyDetails.status;

    let advancedStudyResultsSection;
    if (advancedStudyDetails !== "loading") {
        const advancedStudyConstraintStacks =
            advancedStudyDetails.constraintStacks;

        // Dedupe constraints by monitoredFacilityId for cost and count calculations
        const uniqueConstraintsMap = new Map<
            MonitoredFacilityId,
            AdvancedStudyConstraintStack
        >();
        advancedStudyConstraintStacks.forEach((result) => {
            if (!uniqueConstraintsMap.has(result.monitoredFacilityId)) {
                uniqueConstraintsMap.set(result.monitoredFacilityId, result);
            }
        });
        const uniqueConstraints = Array.from(uniqueConstraintsMap.values());

        const totalCost = uniqueConstraints.reduce(
            (sum, result) => sum + result.cost,
            0
        );
        const constraintCount = uniqueConstraints.length;
        const tableBody = advancedStudyConstraintStacks.map((result) => (
            <tr key={`${result.monitoredFacilityName}-${result.niraBusId}`}>
                <td>{result.monitoredFacilityName}</td>
                <td>{result.rating}</td>
                <td>{formatBenchPercentLoading(result.benchPercentLoading)}</td>
                <td>{`${result.studyPercentLoading.toFixed(2)}%`}</td>
                <td>{getFormattedCost(result.cost)}</td>
                <td>{niraBusIdToBusId(result.niraBusId)}</td>
                <td>{formatPercentString(result.dfax, 2)}</td>
                <td>
                    <Tooltip content={result.contingency} placement="left">
                        <div className="ViewAdvancedStudyResults-table-ellipsize">
                            {result.contingency}
                        </div>
                    </Tooltip>
                </td>
            </tr>
        ));
        advancedStudyResultsSection = (
            <>
                <H5>Results</H5>
                <div className="ViewAdvancedStudyResults-results-summary">
                    <Card className="ViewAdvancedStudyResults-results-summary-card">
                        <H6>Total Cost</H6>
                        <p className="ViewAdvancedStudyResults-results-summary-value">
                            {getFormattedCost(totalCost)}
                        </p>
                    </Card>
                    <Card className="ViewAdvancedStudyResults-results-summary-card">
                        <H6>Constraints</H6>
                        <p className="ViewAdvancedStudyResults-results-summary-value">
                            {constraintCount}
                        </p>
                    </Card>
                </div>
                <HTMLTable striped className="ViewAdvancedStudyResults-table">
                    <thead>
                        <tr>
                            <th>Monitored Facility</th>
                            <th>Rating (MVA)</th>
                            <th>Bench Final AC Loading</th>
                            <th>Study Final AC Loading</th>
                            <th>Cost</th>
                            <th>Bus</th>
                            <th>Dfax</th>
                            <th>Contingency</th>
                        </tr>
                    </thead>
                    <tbody>{tableBody}</tbody>
                </HTMLTable>
            </>
        );
    }

    return (
        <div className="ViewAdvancedStudyResults">
            <Button
                icon={IconNames.ARROW_LEFT}
                variant="minimal"
                onClick={onBack}
                className="ViewAdvancedStudyResults-back-button"
            >
                Back to Studies
            </Button>
            <div className="ViewAdvancedStudyResults-header">
                <H3>{advancedStudy.title}</H3>
                <Tag
                    intent={STATUS_TO_INTENT[advancedStudyStatus]}
                    size="large"
                >
                    {STATUS_TO_DISPLAY_STRING[advancedStudyStatus]}
                </Tag>
            </div>
            <H5>Load Assumptions</H5>
            <HTMLTable striped className="ViewAdvancedStudyResults-table">
                <thead>
                    <tr>
                        <th>Bus #</th>
                        <th>Bus Name</th>
                        <th>Bench Load (MW)</th>
                        <th>Study Load (MW)</th>
                    </tr>
                </thead>
                <tbody>
                    {advancedStudy.loads.map((load) => (
                        <tr key={load.niraBusId}>
                            <td>{niraBusIdToBusId(load.niraBusId)}</td>
                            <td>{load.busDisplayName}</td>
                            <td>{load.benchMwSize ?? "—"}</td>
                            <td>{load.studyMwSize ?? "—"}</td>
                        </tr>
                    ))}
                </tbody>
            </HTMLTable>
            {advancedStudyResultsSection}
        </div>
    );
};

export default ViewAdvancedStudyResults;
